// src/layout/builders/Payment/BuilderProjectValueSummary.jsx
import React, { useEffect, useMemo } from "react";
import { IndianRupee } from "lucide-react";
import usePaymentStore from "../../../store/builders/usePaymentStore";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BuilderProjectValueSummary = ({ projectId }) => {
  const {
    projectValueEntries,
    isValueEntriesLoading,
    fetchProjectValueEntries,
  } = usePaymentStore();

  useEffect(() => {
    if (projectId) {
      fetchProjectValueEntries(projectId);
    }
  }, [projectId, fetchProjectValueEntries]);

  const latestProjectValue = useMemo(() => {
    // Find the most recent entry that has been approved. Since the list is pre-sorted
    // from newest to oldest, .find() will correctly get the latest approved entry.
    const latestApprovedEntry = projectValueEntries.find(
      (entry) => entry.status === "Approved"
    );
    if (latestApprovedEntry) {
      return parseFloat(
        latestApprovedEntry.amount?.toString().replace(/,/g, "") || "0"
      );
    }
    // If no approved entries, find the oldest entry as the initial value
    if (projectValueEntries.length > 0) {
      const oldestEntry = projectValueEntries[projectValueEntries.length - 1];
      return parseFloat(
        oldestEntry.amount?.toString().replace(/,/g, "") || "0"
      );
    }
    return 0; // Return 0 if no entries are found at all
  }, [projectValueEntries]);

  if (isValueEntriesLoading && projectValueEntries.length === 0) {
    return (
      <div className="p-4 sm:p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg shadow-xl mb-6 h-36 flex items-center justify-center">
        <AiOutlineLoading3Quarters className="animate-spin w-8 h-8 text-white" />
        <span className="ml-3 text-sm">Loading current project value...</span>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 bg-gradient-to-br from-neutral-900 to-neutral-700 text-white rounded-lg shadow-xl mb-6">
      <div className="flex flex-col sm:flex-row justify-between items-center">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold mb-1">
          Total Project Value
          </h2>
          <p className="text-xs sm:text-sm text-white-200">
            This is the latest total project value approved for this project.
          </p>
        </div>
        <div className="mt-3 sm:mt-0 text-right">
          <p className="text-3xl sm:text-4xl font-bold flex items-center justify-end">
            <IndianRupee className="w-7 h-7 sm:w-8 sm:h-8 mr-1" />
            {latestProjectValue.toLocaleString("en-IN")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BuilderProjectValueSummary;
